import { Component, h, Host, Element, Event, EventEmitter, Prop, State, Listen } from '@stencil/core';
import { angle_down } from 'pn-design-assets/pn-assets/icons.js';
import { reduceMotion, ripple } from '@/index';

/**
 * Use this row component inside a `pn-accordion` to create an accordion group.
 *
 * **Note**: you can use the `pn-accordion-row` without the parent accordion.
 * But you will have to style the gap between each row.
 *
 * @slot label - Include your own HTML as the label.
 * ```
 * <h3 slot="label">Custom title</h3>
 * ```
 */
@Component({
  tag: 'pn-accordion-row',
  styleUrl: 'pn-accordion-row.scss',
})
export class PnAccordionRow {
  private details: HTMLDetailsElement;
  private summary: HTMLElement;
  private content: HTMLDivElement;
  private animation: Animation;

  private readonly defaultAnimationDuration: number = 400;
  private animationDuration: number = this.defaultAnimationDuration;

  @Element() hostElement: HTMLElement;

  @State() isClosing = false;
  @State() isExpanding = false;

  /** The label/title of the accordion row, you can also pass a slot with the name "label" if you want to put custom HTML content in there. */
  @Prop({ mutable: true }) label: string;
  /** The open/close status of the accordion row. */
  @Prop({ mutable: true }) state?: boolean = false;

  /** Optional unique id for the `summary` element. @category ID */
  @Prop() buttonid?: string;
  /** Optional unique id for the `div` element containing the content. @category ID */
  @Prop() contentid?: string;
  /** Optional prop that removes the default white background on the accordion row */
  @Prop() transparent: boolean = false;

  /** Dispatched every time the row is toggled. Includes the row element and the boolean `state` prop. */
  @Event() togglerow: EventEmitter<{ element: HTMLElement; state: Boolean }>;

  /** Internal function for closing rows when using the `single` prop on the `pn-accordion`. */
  @Listen('rowstate')
  handleRowState(event: CustomEvent) {
    if (event.detail !== this.state) {
      this.details.style.overflow = 'hidden';
      this.closeAccordion();
    }
  }

  componentWillLoad() {
    const slottedLabel = !!this.hostElement.querySelector('[slot="label"]');
    this.label = slottedLabel ? null : this.label;
  }

  private toggleOpen(open?: boolean) {
    const state = open ?? !this.state;
    if (this.state !== open) this.togglerow.emit({ element: this.details, state });
    this.state = state;
  }

  private clickHandler(e: MouseEvent) {
    e.preventDefault();

    this.details.style.overflow = 'hidden';

    if (this.isClosing || !this.state) this.openAccordion();
    else if (this.isClosing || this.state) this.closeAccordion();

    const { x, width, y, top } = this.hostElement.getBoundingClientRect();
    const clientCor = e.clientX === 0 && e.clientY === 0 ? { clientX: x + width - 24, clientY: y - top } : e;

    ripple(clientCor, this.hostElement, '.pn-accordion-row-text');
  }

  private openAccordion() {
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.toggleOpen(true);

    requestAnimationFrame(() => {
      this.isExpanding = true;

      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      this.cancelAnimations();

      this.animate(true, startHeight, endHeight);
    });
  }

  private closeAccordion() {
    this.isClosing = true;

    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    this.cancelAnimations();

    this.animate(false, startHeight, endHeight);
  }

  private animate(open: boolean, startHeight: string, endHeight: string) {
    if (reduceMotion()) this.animationDuration = 0;
    else this.animationDuration = this.defaultAnimationDuration;

    this.animation = this.details.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: this.animationDuration,
        easing: 'cubic-bezier(0.6, 0, 0.2, 1)',
      },
    );

    this.animation.onfinish = () => this.animationFinish(open);

    this.animation.oncancel = () => {
      if (open) this.isExpanding = false;
      else this.isClosing = false;
    };
  }

  private animationFinish(open: boolean) {
    this.toggleOpen(open);
    this.cancelAnimations();

    this.isClosing = false;
    this.isExpanding = false;

    this.details.style.height = '';
    this.details.style.overflow = '';
  }

  private cancelAnimations() {
    if (this.animation) this.animation.cancel();
  }

  render() {
    return (
      <Host>
        <details class="pn-accordion-row" open={this.state} ref={(el: HTMLDetailsElement) => (this.details = el)}>
          <summary
            id={this.buttonid}
            class="pn-accordion-row-summary"
            onClick={e => this.clickHandler(e)}
            ref={el => (this.summary = el)}
          >
            <div class="pn-accordion-row-text" data-transparent={this.transparent}>
              {this.label ? this.label : ''}
              <slot name="label" />

              <pn-icon class="pn-accordion-row-icon" icon={angle_down} />
            </div>
          </summary>

          <div id={this.contentid} class="pn-accordion-row-content" ref={(el: HTMLDivElement) => (this.content = el)}>
            <slot />
          </div>
        </details>
      </Host>
    );
  }
}
